import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'
import { RealtimePostgresChangesPayload } from '@supabase/realtime-js'
import { Database } from '../types/supabase'
import { supabase } from '../supabaseClient'
import { isEmpty, pickFields } from '../utilities/general'
import { callTool } from '../lib/tools'
import { TOKEN_SELECT } from '../utilities/enrollmentKey'
import useNetwork from './useNetwork'

export interface TokenState {
  tokens: Token[]
  set: (state: Partial<TokenState>) => void
  get: () => Promise<void>
  add: (token: Token) => void
  update: (token: Partial<Token>) => Promise<void>
  /** Creates a key through `network_key_create` and returns its id. Throws the router's refusal. */
  create: (networkId: string, name: string) => Promise<string>
  /** Renames a key or turns it on or off through `network_key_update`. Throws the router's refusal. */
  change: (id: string, change: { name?: string; enabled?: boolean }) => Promise<void>
  /** Deletes a key through `network_key_delete`. Throws the router's refusal. */
  delete: (id: string) => Promise<void>
  /**
   * The org of key `id`: the org of its network. Two direct reads: the key's network, then that
   * network (`useNetwork.orgOf`). `false` when the reader can read no such key, null when a read
   * failed (app#477).
   */
  orgOf: (id: string) => Promise<string | false | null>
  subscribe: () => () => void
  reset: () => void
}

const defaultToken: Token = {
  createdAt: '',
  usedAt: '',
  name: '',
  networkId: '',
  enabled: true,
}

// ⚠⚠ NO `secret` HERE (app#379): router migration 0216 withholds the column, and a select that
// names it fails as a whole. The secret is read through `network_key_read` where it is shown
// (`utilities/enrollmentKey.ts`).
const tokenFields = TOKEN_SELECT.split(', ')

const state = {
  tokens: [],
}

const useToken = create<TokenState>()((set, get) => ({
  ...state,
  set,
  orgOf: async id => {
    const { data, error } = await supabase.from('tokens').select('network_id').eq('id', id).limit(1)
    if (error?.code === '22P02') return false // not a uuid: no such key
    if (error) {
      console.warn(error)
      return null
    }
    const networkId = (data ?? [])[0]?.network_id
    if (!networkId) return false
    return useNetwork.getState().orgOf(networkId)
  },
  get: async () => {
    const { data, error } = await supabase.from('tokens').select(TOKEN_SELECT)
    if (error) console.warn(error)
    else {
      const tokens: Token[] = data ? data.map(token => camelcaseKeys(token) as Token) : []
      set({ tokens })
      console.log('get tokens', tokens)
    }
  },
  add: token => {
    const tokens = [...get().tokens, token]
    set({ tokens })
  },
  update: async token => {
    const tokens: Token[] = [...get().tokens]
    const { id, ...data } = token

    const index = tokens.findIndex(t => t.id === id)
    if (index === -1) return
    if (isEmpty(data)) {
      tokens.splice(index, 1)
    } else {
      tokens[index] = { ...defaultToken, ...tokens[index], ...token }
    }

    set({ tokens })
  },
  // ⚠⚠ KEY WRITES GO THROUGH THE ROUTER'S `network_key_*` TOOLS (app#373, docs/PERMISSIONS.md
  // § 5 Network: "Read, create, disable, delete an enrollment key — network owner or Admin"). They
  // were PostgREST upserts and deletes, and the upsert sent the whole row back, secret included
  // (`onEnable` spread the token). The router makes the secret itself and never takes one.
  // ⚠ The new row reaches this store through realtime, as before.
  create: async (networkId, name) => {
    const key = await callTool<{ key_id: string }>('network_key_create', { network_id: networkId, name })
    return key.key_id
  },
  change: async (id, change) => {
    await callTool('network_key_update', { key_id: id, ...change })
  },
  delete: async id => {
    await callTool('network_key_delete', { key_id: id })
  },
  subscribe: () => {
    const { add, update } = get()

    const subscription = supabase
      .channel('tokens-all')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tokens' },
        (payload: RealtimePostgresChangesPayload<Database['public']['Tables']['tokens']['Row']>) => {
          // ⚠ Log the event and the key id only, never the payload: before router migration 0216
          // applies, the row carries `secret`, and a console line can reach a session recording.
          console.log('Token change received!', payload.eventType, (payload.new as { id?: string }).id ?? (payload.old as { id?: string }).id)
          const token = camelcaseKeys(pickFields(payload.new, tokenFields)) as Token

          switch (payload.eventType) {
            case 'INSERT':
              add(token)
              break
            case 'UPDATE':
              update(token)
              break
            case 'DELETE':
              update({ id: payload.old.id })
              break
          }
        }
      )
      .subscribe()

    return () => {
      console.log('Unsubscribing from token changes')
      supabase.removeChannel(subscription)
    }
  },
  reset: () => set({ ...state }),
}))

export default useToken
